import { useState, useMemo, useCallback, useRef } from 'react'

import { Box } from '@mui/material'
import { GridRowId, GridSortItem, useGridApiRef } from '@mui/x-data-grid'
import { toast } from 'react-toastify'

import { FplDataGrid, ConfirmDialog } from 'components/common'
import { CourierExchangeManualAcceptFeatureName } from 'constants/EntityConstants/AppFeature'
import {
  BookingDriver,
  GetBookingAllocatedDriversDocument,
  SortEnumType,
  useDeleteBookingDriverMutation,
  useGetAppFeatureByNameQuery,
  useUpdateDriverAllocationStatusAsAcceptedManuallyMutation,
} from 'generated/graphql'
import {
  GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
  GET_PAGED_SUPPLIERS_BOOKING_ALLOCATED_DRIVERS,
} from 'graphql/queries'
import useGetAllocatedDriversColumns from 'hooks/fplDataGridColumns/useGetAllocatedDriversColumns'
import { usePermission } from 'providers'

import { AddRatingDialog } from './AddRatingDialog'
import { CustomGridFooter } from './CustomGridFooter'
import { DriverNoteDialog } from './DriverNoteDialog'
import { EditDriverDialog } from './EditDriverDialog'
import { ReplaceDriverDialog } from './ReplaceDriverDialog'

interface IProps {
  bookingId?: string
  bookingStatus?: any
  courierExchangeLoadId?: string
}

const AllocatedDriverDetails = (props: IProps) => {
  const { bookingId, bookingStatus, courierExchangeLoadId } = props

  // States
  const gridApiRef = useGridApiRef()

  const fplDataGridRef = useRef<any>()
  const [bookingDriver, setBookingDriver] = useState<BookingDriver | null>(null)
  const [driverId, setDriverId] = useState<number | null>(null)
  const [openEditDriverDialog, setOpenEditDriverDialog] = useState(false)
  const [driverNoteDialog, setDriverNoteDialog] = useState<{ open: boolean; note: string }>({
    open: false,
    note: '',
  })
  const [openRatingDialog, setOpenRatingDialog] = useState(false)
  const [openCxManualAcceptDialog, setOpenCxManualAcceptDialog] = useState(false)

  // Permissions
  const [isAllowedToViewPrimaryDriversList] = usePermission('ViewPrimaryDriversList')
  const [isAllowedToViewCurrentSupplierSubDriversList] = usePermission('ViewSupplierSubDriversList')
  const [isAllowedToUpdateBookingStatusCourierExchange] = usePermission(
    'CourierExchangeUpdateBookingStatus',
  )

  const queryData = useMemo(() => {
    const variables = {
      bookingId: Number(bookingId),
      order: { createdAt: SortEnumType.Desc },
      after: null,
      before: null,
      first: 100,
      last: null,
    }

    const queryVariables = { bookingId: Number(bookingId) }

    const defaultOrder = { field: 'createdAt', sort: 'desc' } as GridSortItem

    const pageOptions = { pageSize: 100, rowsPerPage: [100] }

    if (isAllowedToViewPrimaryDriversList) {
      return {
        query: GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
        entityName: 'pagedBookingAllocatedDrivers',
        queryVariables,
        defaultOrder,
        pageOptions,
        variables,
        refetchQuery: {
          query: GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
          variables,
        },
      }
    }
    if (isAllowedToViewCurrentSupplierSubDriversList) {
      return {
        query: GET_PAGED_SUPPLIERS_BOOKING_ALLOCATED_DRIVERS,
        entityName: 'pagedSupplierBookingAllocatedDrivers',
        queryVariables,
        defaultOrder,
        pageOptions,
        variables,
        refetchQuery: {
          query: GET_PAGED_SUPPLIERS_BOOKING_ALLOCATED_DRIVERS,
          variables,
        },
      }
    }

    return null
  }, [isAllowedToViewPrimaryDriversList, isAllowedToViewCurrentSupplierSubDriversList, bookingId])

  const updateSelectedAllocationStatus = (
    updateQueryData: any,
    result: any,
    variablesContext: any,
  ) => {
    if (updateQueryData && updateQueryData?.bookingAllocatedDrivers) {
      return {
        bookingAllocatedDrivers: updateQueryData?.bookingAllocatedDrivers.filter(
          (item) => Number(item.id) !== Number(variablesContext.variables?.bookingDriverId),
        ),
      }
    }

    return queryData
  }

  const { data: appFeatureData } = useGetAppFeatureByNameQuery({
    variables: { name: CourierExchangeManualAcceptFeatureName },
    fetchPolicy: 'network-only',
    skip: !isAllowedToUpdateBookingStatusCourierExchange,
  })
  const isCxManualAcceptEnabled = appFeatureData?.appFeatureByName?.appFeatureStatus ?? false

  // Mutations
  const [deleteBookingDriver, { loading: deleteBookingDriverProcessing }] =
    useDeleteBookingDriverMutation({
      refetchQueries: [queryData?.refetchQuery],
      update: (cache, result, variablesContext) => {
        cache.evict({
          id: cache.identify({ __typename: 'Booking', id: Number(bookingId) }),
          fieldName: 'hasWinningBidsAwaitingAllocation',
        })
        cache.updateQuery(
          {
            query: GetBookingAllocatedDriversDocument,
            variables: {
              bookingId: Number(bookingId),
            },
          },
          (updateQueryData) => {
            return updateSelectedAllocationStatus(updateQueryData, result, variablesContext)
          },
        )
      },
      onCompleted: () => {
        toast.success('Driver was deleted with success.')
      },
    })

  const [updateDriverAllocationStatusAsAcceptedManually] =
    useUpdateDriverAllocationStatusAsAcceptedManuallyMutation({
      refetchQueries: [queryData?.refetchQuery],
      onCompleted: () => {
        toast.success('Booking was manually accepted as CX with success.')
      },
      onError: (error) => {
        console.error(error)
      },
    })

  // Handlers
  const handleCloseDriverNoteDialog = () => {
    setDriverNoteDialog({
      note: '',
      open: false,
    })
  }

  const handleDriverNotes = useCallback(
    (note: string) => () => {
      setDriverNoteDialog({
        note,
        open: true,
      })
    },
    [],
  )

  const handleDeleteDriver = useCallback(
    (id: GridRowId) => () => {
      deleteBookingDriver({
        variables: { bookingDriverId: Number(id) },
      })
    },
    [deleteBookingDriver],
  )

  const handleEditAllocatedDriver = useCallback(
    (row: BookingDriver) => () => {
      setBookingDriver(row)
      setOpenEditDriverDialog(true)
    },
    [],
  )

  const handleCloseEditDriverDialog = () => {
    setOpenEditDriverDialog(false)
  }

  const handleAddRating = useCallback(
    (id: GridRowId) => () => {
      setDriverId(Number(id))
      setOpenRatingDialog(true)
    },
    [],
  )

  const handleCloseAddRatingDialog = () => setOpenRatingDialog(false)

  const handleCxManualAcceptOpenDialog = () => setOpenCxManualAcceptDialog(true)
  const handleCxManualAcceptConfirmation = () => {
    updateDriverAllocationStatusAsAcceptedManually({
      variables: {
        bookingId: Number(bookingId),
      },
    })
    setOpenCxManualAcceptDialog(false)
  }

  const columns = useGetAllocatedDriversColumns(
    isAllowedToViewPrimaryDriversList,
    isAllowedToViewCurrentSupplierSubDriversList,
    handleDriverNotes,
    deleteBookingDriverProcessing,
    handleEditAllocatedDriver,
    handleAddRating,
    handleDeleteDriver,
    bookingStatus,
    isCxManualAcceptEnabled,
    handleCxManualAcceptOpenDialog,
    courierExchangeLoadId,
  )

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-root': {
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            WebkitTransform: 'translate3d(0, 0, 0)',
            transform: 'translate3d(0, 0, 0)',
          },
          '& .MuiDataGrid-virtualScroller': {
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          },
          '& .MuiLinearProgress-root': {
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          },
        }}>
        <FplDataGrid
          gridApiRef={gridApiRef}
          ref={fplDataGridRef}
          query={queryData?.query}
          entityName={queryData?.entityName ?? ''}
          columns={columns}
          queryVariables={queryData?.queryVariables}
          defaultOrder={queryData?.defaultOrder}
          pageOptions={queryData?.pageOptions}
          toolbar={{ caption: 'Allocated Drivers' }}
          footer={
            !isAllowedToViewPrimaryDriversList
              ? {
                  component: CustomGridFooter,
                  componentProps: {
                    bookingId: Number(bookingId),
                    queryVariables: queryData?.variables,
                  },
                }
              : undefined
          }
        />
      </Box>

      {bookingDriver?.id && isAllowedToViewPrimaryDriversList && (
        <EditDriverDialog
          bookingId={Number(bookingId)}
          bookingDriverId={Number(bookingDriver.id)}
          openDialog={openEditDriverDialog}
          handleCloseDialog={handleCloseEditDriverDialog}
        />
      )}

      {bookingDriver?.id && isAllowedToViewCurrentSupplierSubDriversList && (
        <ReplaceDriverDialog
          gridApiRef={gridApiRef}
          fplDataGridRef={fplDataGridRef}
          bookingDriver={bookingDriver}
          openDialog={openEditDriverDialog}
          handleCloseDialog={handleCloseEditDriverDialog}
        />
      )}

      {driverId && bookingId && (
        <AddRatingDialog
          driverId={driverId}
          bookingId={Number(bookingId)}
          openDialog={openRatingDialog}
          handleCloseDialog={handleCloseAddRatingDialog}
        />
      )}

      {isCxManualAcceptEnabled && isAllowedToUpdateBookingStatusCourierExchange && (
        <ConfirmDialog
          key={'cx-manual-accept-confirmation'}
          open={openCxManualAcceptDialog}
          onConfirm={handleCxManualAcceptConfirmation}
          setOpen={setOpenCxManualAcceptDialog}
          title={`Manual Accept Booking as CX Driver`}
          message={`You are about to manually accept this booking as a CX driver. 
            Please note this functionality is meant for testing purposes only and it should not be used in a production environment. 
            Do you want to proceed?`}
          option1='Cancel'
          option2='Confirm'
        />
      )}

      <DriverNoteDialog {...driverNoteDialog} onClose={handleCloseDriverNoteDialog} />
    </>
  )
}

export default AllocatedDriverDetails
